import { useMemo } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { computeDimOverlayGeom } from '../helpers/dim-overlay-geom'

/**
 * Which shape the node-edit and crop chrome (dim overlay, overlay, toolbars) is drawn for — the
 * edited shape, unless its geometry is locked/frozen — and each mode's dim-overlay geometry.
 */
export function useEditModeChrome({
  isNodeEditMode,
  nodeEditShapeData,
  isCropMode,
  cropShapeData,
  isNodeEditShapeGeometryBlocked,
  bleedWidth,
  bleedHeight,
}: {
  isNodeEditMode: boolean
  nodeEditShapeData: ShapeData | null
  isCropMode: boolean
  cropShapeData: ShapeData | null
  isNodeEditShapeGeometryBlocked: (shape: ShapeData) => boolean
  bleedWidth: number
  bleedHeight: number
}) {
  const nodeEditDimGeom = useMemo(
    () => computeDimOverlayGeom(nodeEditShapeData, bleedWidth, bleedHeight),
    [nodeEditShapeData, bleedWidth, bleedHeight]
  )

  const cropDimGeom = useMemo(
    () => computeDimOverlayGeom(cropShapeData, bleedWidth, bleedHeight),
    [cropShapeData, bleedWidth, bleedHeight]
  )

  // Computed once per render for every element of each mode's chrome (the dim overlay, overlay and
  // toolbars all share the guard). Reads the live ShapeManager, so it is not memoized.
  const nodeEditChromeShape =
    isNodeEditMode && nodeEditShapeData && !isNodeEditShapeGeometryBlocked(nodeEditShapeData)
      ? nodeEditShapeData
      : null
  const cropChromeShape =
    isCropMode && cropShapeData && !isNodeEditShapeGeometryBlocked(cropShapeData)
      ? cropShapeData
      : null

  return { nodeEditChromeShape, nodeEditDimGeom, cropChromeShape, cropDimGeom }
}
